import React, { useEffect, useState } from 'react'
import { Alert, Badge, Breadcrumb, Button, Card, Col, Form, Modal, Row, Spinner } from 'react-bootstrap'
import { FaLock, FaTimes } from 'react-icons/fa'
import CreatableSelect from 'react-select/creatable'
import { useNavigate, useParams } from 'react-router-dom'
import useFetch from '../../../../_fetch'
import { currencies } from '../../../../_utils'
import { IPartnership, PartnershipType } from '../types'

function getPayoutLabel(partnership: IPartnership) {
    if (partnership.type === 'sale') return `${partnership.value}% of sale`
    if (partnership.type === 'click') return `${partnership.value} ${partnership.currency} per click`
    return `${partnership.value} ${partnership.currency}`
}

function getValueLabel(type: PartnershipType) {
    if (type === 'sale') return 'Sale percentage'
    if (type === 'click') return 'Value per click'
    return 'Value'
}

function stripCommonSubdomain(hostname: string) {
    return hostname.replace(/^www\d*\./i, '')
}

function cleanDomain(input: string) {
    try {
        const url = input.startsWith('http') ? input : `https://${input}`
        const parsed = new URL(url)
        return stripCommonSubdomain(parsed.hostname.toLowerCase())
    } catch {
        return null
    }
}

function isValidDomain(domain: string) {
    return /^[a-z0-9.-]+\.[a-z]{2,}$/i.test(domain)
}

function getApiErrorMessage(err: any, fallback: string) {
    if (typeof err !== 'string') return fallback

    try {
        const parsed = JSON.parse(err)
        if (typeof parsed?.error === 'string' && parsed.error.trim()) return parsed.error
        if (typeof parsed?.message === 'string' && parsed.message.trim()) return parsed.message
    } catch {
        // Use plain text below.
    }

    return err.trim() || fallback
}

const PartnershipDetail: React.FC = () => {
    const { id } = useParams()
    const navigate = useNavigate()
    const { doFetch } = useFetch()

    const [loading, setLoading] = useState(true)
    const [saving, setSaving] = useState(false)
    const [deleting, setDeleting] = useState(false)
    const [downloadingPdf, setDownloadingPdf] = useState(false)
    const [revoking, setRevoking] = useState(false)
    const [isEditing, setIsEditing] = useState(false)
    const [error, setError] = useState('')
    const [success, setSuccess] = useState('')
    const [domainError, setDomainError] = useState('')
    const [showDeleteModal, setShowDeleteModal] = useState(false)
    const [showRevokeModal, setShowRevokeModal] = useState(false)
    const [partnership, setPartnership] = useState<IPartnership | null>(null)
    const [formDomains, setFormDomains] = useState<string[]>([])

    const [form, setForm] = useState({
        name: '',
        type: 'sale' as PartnershipType,
        value: '',
        currency: 'DKK',
        startDate: '',
        expiryDate: '',
        cookieDays: '30',
    })

    useEffect(() => {
        const fetchPartnership = async () => {
            try {
                setLoading(true)
                setError('')

                const res = await doFetch(`/byelink/partnerships/${id}`)
                if (res.status >= 400) throw await res.text()

                const data = await res.json()
                setPartnership(data)
                setForm({
                    name: data.name || '',
                    type: data.type || 'sale',
                    value: String(data.value ?? ''),
                    currency: data.currency || 'DKK',
                    startDate: data.startDate || '',
                    expiryDate: data.expiryDate || '',
                    cookieDays: String(data.cookieDays ?? 30),
                })
                setFormDomains(Array.isArray(data.domains) ? data.domains : [])
            } catch (err: any) {
                setError(getApiErrorMessage(err, 'Could not load partnership'))
            } finally {
                setLoading(false)
            }
        }

        if (id) fetchPartnership()
    }, [doFetch, id])

    const updateForm = (key: keyof typeof form, value: string) => {
        if (key === 'type') {
            setForm((prev) => {
                const nextType = value as PartnershipType
                if (nextType === 'click') {
                    return { ...prev, type: nextType, cookieDays: '0' }
                }
                if (nextType === 'sale') {
                    return {
                        ...prev,
                        type: nextType,
                        currency: 'DKK',
                        cookieDays: prev.cookieDays === '0' ? '30' : prev.cookieDays,
                    }
                }
                return {
                    ...prev,
                    type: nextType,
                    cookieDays: prev.cookieDays === '0' ? '30' : prev.cookieDays,
                }
            })
            return
        }

        setForm((prev) => ({ ...prev, [key]: value }))
    }

    const handleStartEdit = () => {
        if (!partnership) return

        const locked = partnership.locked === true || String((partnership as any).locked).toLowerCase() === 'true'
        if (locked) {
            setError('This partnership is locked by advertiser and cannot be edited.')
            setSuccess('')
            return
        }

        setIsEditing(true)
        setError('')
        setSuccess('')
    }

    const addDomain = (option: any) => {
        const raw = option?.value?.trim()?.toLowerCase()
        if (!raw) return

        const domain = cleanDomain(raw)
        if (!domain || !isValidDomain(domain)) {
            setDomainError('Invalid domain')
            return
        }

        setDomainError('')
        setFormDomains((current) => (current.includes(domain) ? current : [...current, domain]))
    }

    const removeDomain = (domain: string) => {
        if (!isEditing) return
        setFormDomains((current) => current.filter((entry) => entry !== domain))
    }

    const handleCancelEdit = () => {
        if (!partnership) return

        setForm({
            name: partnership.name || '',
            type: partnership.type || 'sale',
            value: String(partnership.value ?? ''),
            currency: partnership.currency || 'DKK',
            startDate: partnership.startDate || '',
            expiryDate: partnership.expiryDate || '',
            cookieDays: String(partnership.cookieDays ?? 30),
        })
        setFormDomains(Array.isArray(partnership.domains) ? partnership.domains : [])
        setDomainError('')
        setError('')
        setSuccess('')
        setIsEditing(false)
    }

    const handleDownloadPdf = async () => {
        if (!partnership) return
        const canDownloadIntegrationPdf = partnership.type === 'sale' || partnership.type === 'lead'
        if (!canDownloadIntegrationPdf) {
            setError('Integration PDF is only available for Sale and Lead partnerships')
            setSuccess('')
            return
        }

        try {
            setDownloadingPdf(true)
            setError('')
            setSuccess('')

            const res = await doFetch(`/byelink/partnerships/${partnership.id}/scripts-pdf`)
            if (res.status >= 400) throw await res.text()

            const blob = await res.blob()
            const fileName = `${(partnership.name || 'partnership').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '') || 'partnership'}-integration.pdf`

            const objectUrl = URL.createObjectURL(blob)
            const anchor = document.createElement('a')
            anchor.href = objectUrl
            anchor.download = fileName
            document.body.appendChild(anchor)
            anchor.click()
            anchor.remove()
            URL.revokeObjectURL(objectUrl)

            setSuccess('Integration PDF downloaded')
        } catch (err: any) {
            setError(getApiErrorMessage(err, 'Could not download integration PDF'))
        } finally {
            setDownloadingPdf(false)
        }
    }

    const handleSave = async () => {
        if (!partnership) return

        const locked = partnership.locked === true || String((partnership as any).locked).toLowerCase() === 'true'
        if (locked) {
            setError('This partnership is locked by advertiser and cannot be edited.')
            setSuccess('')
            return
        }

        setError('')
        setSuccess('')

        if (!form.name.trim()) {
            setError('Partner name is required')
            return
        }

        if (!form.startDate) {
            setError('Start date is required')
            return
        }

        const valueNumber = Number(form.value)
        if (!Number.isFinite(valueNumber) || valueNumber < 0) {
            setError('Value must be a positive number')
            return
        }

        const cookieDays = form.type === 'click' ? 0 : Number(form.cookieDays)
        if (form.type !== 'click' && (!Number.isInteger(cookieDays) || cookieDays <= 0)) {
            setError('Cookie duration must be at least 1 day')
            return
        }
        if (form.type !== 'click' && cookieDays > 60) {
            setError('Cookie duration cannot exceed 60 days')
            return
        }

        try {
            setSaving(true)

            const payload = {
                id: partnership.id,
                name: form.name,
                domains: formDomains,
                type: form.type,
                value: valueNumber,
                currency: form.type === 'sale' ? 'DKK' : form.currency,
                startDate: form.startDate,
                expiryDate: form.expiryDate || null,
                cookieDays,
            }

            const res = await doFetch('/byelink/partnerships/update', payload)
            if (res.status >= 400) throw await res.text()

            const updated = await res.json()
            setPartnership(updated)
            setSuccess('Partnership saved')
            setIsEditing(false)
        } catch (err: any) {
            setError(getApiErrorMessage(err, 'Could not save partnership'))
        } finally {
            setSaving(false)
        }
    }

    const handleDelete = async () => {
        if (!partnership) return

        try {
            setDeleting(true)
            setError('')
            setSuccess('')

            const res = await doFetch('/byelink/partnerships/delete', { id: partnership.id })
            if (res.status >= 400) throw await res.text()

            navigate('/link-script/partnerships')
        } catch (err: any) {
            setError(getApiErrorMessage(err, 'Could not delete partnership'))
        } finally {
            setDeleting(false)
            setShowDeleteModal(false)
        }
    }

    const handleRevokeIdentifier = async () => {
        if (!partnership) return

        try {
            setRevoking(true)
            setError('')
            setSuccess('')

            const res = await doFetch(`/byelink/partnerships/${partnership.id}/regenerate-secret`, {})
            if (res.status >= 400) throw await res.text()

            const updated = await res.json()
            setPartnership(updated)
            setSuccess('Conversion Secret Key revoked. A new secret key has been generated.')
            setShowRevokeModal(false)
        } catch (err: any) {
            setError(getApiErrorMessage(err, 'Could not revoke Conversion Secret Key'))
        } finally {
            setRevoking(false)
        }
    }

    if (loading) {
        return (
            <div className="d-flex justify-content-center py-5">
                <Spinner animation="border" />
            </div>
        )
    }

    if (!partnership) {
        return <Alert variant="warning">Partnership was not found.</Alert>
    }

    const isLocked = partnership.locked === true || String((partnership as any).locked).toLowerCase() === 'true'
    const canDownloadIntegrationPdf = partnership.type === 'sale' || partnership.type === 'lead'

    return (
        <div className="pe-2">
            {error && <Alert variant="danger">{error}</Alert>}
            {success && <Alert variant="success">{success}</Alert>}

            <Card className="mb-3">
                <Card.Body>
                    <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-2 mb-3 pb-2 border-bottom">
                        <div>
                            <Breadcrumb className="mb-1 small">
                                <Breadcrumb.Item onClick={() => navigate('/link-script/partnerships')}>Partnerships</Breadcrumb.Item>
                                <Breadcrumb.Item active>{partnership.name}</Breadcrumb.Item>
                            </Breadcrumb>
                            <div className="d-flex align-items-center gap-2">
                                <h4 className="mb-0 fw-semibold">{partnership.name}</h4>
                                <Badge bg="secondary">{partnership.type}</Badge>
                            </div>
                            {isLocked ? (
                                <div className="d-flex align-items-center gap-1 text-secondary small mt-1">
                                    <FaLock style={{ fontSize: '0.75rem' }} />
                                    <span>Locked by advertiser — contact the advertiser to make changes.</span>
                                </div>
                            ) : (
                                <div className="text-muted small">Update payout and tracking settings.</div>
                            )}
                        </div>
                        <div className="d-flex align-items-center gap-2">
                            {!isEditing && !isLocked ? (
                                <Button variant="primary" onClick={handleStartEdit}>
                                    Edit
                                </Button>
                            ) : isEditing ? (
                                <>
                                    <Button variant="outline-secondary" onClick={handleCancelEdit}>
                                        Cancel
                                    </Button>
                                    <Button variant="outline-danger" onClick={() => setShowDeleteModal(true)}>
                                        Delete
                                    </Button>
                                </>
                            ) : null}
                        </div>
                    </div>

                    <Row className="mb-3">
                        <Col md={6}><strong>Payout:</strong> {getPayoutLabel(partnership)}</Col>
                        <Col md={6}><strong>Domains:</strong> {formDomains.length}</Col>
                    </Row>

                    <Row>
                        <Col md={6}>
                            <Form.Group className="mb-3">
                                <Form.Label>Partner name</Form.Label>
                                <Form.Control
                                    value={form.name}
                                    disabled={!isEditing}
                                    onChange={(event) => updateForm('name', event.target.value)}
                                />
                            </Form.Group>
                        </Col>
                        <Col md={6}>
                            <Form.Group className="mb-3">
                                <Form.Label>Partner domains</Form.Label>
                                <CreatableSelect
                                    placeholder="Add domain (e.g. partner.com)"
                                    isClearable
                                    isDisabled={!isEditing}
                                    formatCreateLabel={(value) => `Add "${value}"`}
                                    onChange={isEditing ? addDomain : undefined}
                                    onCreateOption={isEditing ? (value) => addDomain({ value }) : undefined}
                                />
                                <div className="d-flex flex-wrap gap-2 mt-2">
                                    {formDomains.map((domain) => (
                                        <Badge key={domain} bg="info" className="d-flex align-items-center px-2 py-1">
                                            {domain}
                                            <FaTimes
                                                className="ms-2"
                                                style={{ cursor: 'pointer' }}
                                                onClick={() => removeDomain(domain)}
                                            />
                                        </Badge>
                                    ))}
                                </div>
                                {domainError && <div className="text-danger small mt-2">{domainError}</div>}
                            </Form.Group>
                        </Col>
                    </Row>

                    <Row>
                        <Col md={4}>
                            <Form.Group className="mb-3">
                                <Form.Label>Type</Form.Label>
                                <Form.Select
                                    value={form.type}
                                    disabled
                                    onChange={(event) => updateForm('type', event.target.value as PartnershipType)}
                                >
                                    <option value="sale">Sale</option>
                                    <option value="click">Click</option>
                                    <option value="lead" disabled>Lead</option>
                                </Form.Select>
                            </Form.Group>
                        </Col>
                        {form.type !== 'sale' ? (
                            <Col md={4}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Currency</Form.Label>
                                    <Form.Select
                                        value={form.currency}
                                        disabled={!isEditing}
                                        onChange={(event) => updateForm('currency', event.target.value)}
                                    >
                                        {currencies.map((currency) => (
                                            <option key={currency} value={currency}>
                                                {currency}
                                            </option>
                                        ))}
                                    </Form.Select>
                                </Form.Group>
                            </Col>
                        ) : null}
                        <Col md={4}>
                            <Form.Group className="mb-3">
                                <Form.Label>{getValueLabel(form.type)}</Form.Label>
                                <Form.Control
                                    type="number"
                                    step="0.01"
                                    value={form.value}
                                    disabled={!isEditing}
                                    onChange={(event) => updateForm('value', event.target.value)}
                                />
                            </Form.Group>
                        </Col>
                    </Row>

                    <Row>
                        <Col md={4}>
                            <Form.Group className="mb-3">
                                <Form.Label>Start date</Form.Label>
                                <Form.Control
                                    type="date"
                                    value={form.startDate}
                                    disabled={!isEditing}
                                    onChange={(event) => updateForm('startDate', event.target.value)}
                                />
                            </Form.Group>
                        </Col>
                        <Col md={4}>
                            <Form.Group className="mb-3">
                                <Form.Label>End date (optional)</Form.Label>
                                <Form.Control
                                    type="date"
                                    value={form.expiryDate}
                                    disabled={!isEditing}
                                    onChange={(event) => updateForm('expiryDate', event.target.value)}
                                />
                            </Form.Group>
                        </Col>
                        {form.type !== 'click' && (
                            <Col md={4}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Cookie duration (days)</Form.Label>
                                    <Form.Control
                                        type="number"
                                        min={1}
                                        max={60}
                                        value={form.cookieDays}
                                        disabled={!isEditing}
                                        onChange={(event) => updateForm('cookieDays', event.target.value)}
                                    />
                                </Form.Group>
                            </Col>
                        )}
                    </Row>

                    {form.type === 'click' && (
                        <Alert variant="info" className="mb-3">
                            Click type works with link automation only.
                        </Alert>
                    )}

                    {isEditing && (
                        <div className="d-flex justify-content-end">
                            <Button onClick={handleSave} disabled={saving}>
                                {saving ? 'Saving...' : 'Save changes'}
                            </Button>
                        </div>
                    )}
                </Card.Body>
            </Card>

            {partnership.type !== 'click' ? (
                <>
                    <Card className="mb-3">
                        <Card.Header>
                            <strong>Conversion Secret Key</strong>
                        </Card.Header>
                        <Card.Body>
                            <div className="text-muted small mb-3">
                                This secret is sent as <code>conversionIdentifier</code> in conversion requests. Keep it private.
                            </div>
                            <Row className="g-2 align-items-end">
                                <Col md={9}>
                                    <Form.Group>
                                        <Form.Label className="mb-1">Secret key</Form.Label>
                                        <Form.Control
                                            value={partnership.scriptSecret || ''}
                                            disabled
                                            style={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace' }}
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={3}>
                                    <Button
                                        variant="outline-danger"
                                        onClick={() => setShowRevokeModal(true)}
                                        disabled={revoking || isLocked}
                                        className="w-100"
                                    >
                                        {revoking ? 'Revoking...' : 'Revoke key'}
                                    </Button>
                                </Col>
                            </Row>
                            <div className="text-muted small mt-2">
                                Rotating the key immediately invalidates the old one. Update all integrations after revoking.
                            </div>
                            {isLocked ? (
                                <div className="text-muted small mt-1">
                                    This partnership is locked, so key rotation is disabled.
                                </div>
                            ) : null}
                        </Card.Body>
                    </Card>

                    <Card className="mb-3">
                        <Card.Body>
                            <div className="fw-semibold mb-1">Download setup guide (PDF)</div>
                            <div className="text-muted small mb-3">
                                Download the full setup guide with Entry tracker script, Conversion script, and server-side payload example.
                            </div>
                            {!canDownloadIntegrationPdf ? (
                                <div className="text-muted small mb-3">
                                    Integration PDF is currently only available for <strong>Sale</strong> and <strong>Lead</strong> partnerships.
                                </div>
                            ) : null}
                            <Button
                                variant="outline-secondary"
                                onClick={handleDownloadPdf}
                                disabled={downloadingPdf || !canDownloadIntegrationPdf}
                            >
                                {downloadingPdf ? 'Downloading PDF...' : 'Download integration PDF'}
                            </Button>
                        </Card.Body>
                    </Card>
                </>
            ) : null}

            <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>Delete partnership</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    This will permanently delete <strong>{partnership.name}</strong>. This action cannot be undone.
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
                        Cancel
                    </Button>
                    <Button variant="danger" onClick={handleDelete} disabled={deleting}>
                        {deleting ? 'Deleting...' : 'Delete partnership'}
                    </Button>
                </Modal.Footer>
            </Modal>

            <Modal show={showRevokeModal} onHide={() => setShowRevokeModal(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>Revoke Conversion Secret Key</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p className="mb-2">
                        This will immediately revoke the current Conversion Secret Key for <strong>{partnership.name}</strong>.
                    </p>
                    <p className="mb-0 text-muted">
                        Existing integrations using the old secret key will fail until they are updated with the new key.
                    </p>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowRevokeModal(false)} disabled={revoking}>
                        Cancel
                    </Button>
                    <Button variant="danger" onClick={handleRevokeIdentifier} disabled={revoking}>
                        {revoking ? 'Revoking...' : 'Revoke identifier'}
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    )
}

export default PartnershipDetail
